import { IMutateAllocationBagValue } from "actions/collections";
import { OperationType } from "actions/mutate";
import { ValueGetterParams } from "ag-grid-community";
import { IAttributeFilters } from "components/product-collection/product-collection-toolbar/product-collection-toolbar-filters/types";
import {
    ALLOCATION_DIMENSION_ID_PREFIX,
    IGridItem,
    Item,
    ItemBagType,
    Kinds,
    MAX_MUTATION_OPERATION_LIMIT,
    ValueTypes,
} from "constants/product-collection";
import { ISchemaKey } from "helpers/product-collection";
import { fromJS, Map } from "immutable";
import { safeParseFloat, safeParseInt } from "lib/number";
import { debounce, get } from "lodash";
import lodashGet from "lodash/get";
import lodashMemoize from "lodash/memoize";
import memoizeOne from "memoize-one";
import { IBagTable } from "./table-bag";
import { sanitizeDimensionAttribute } from "components/product-collection/product-collection-views/product-collection-grid-view/tool-panels/dimensions-tool-panel/utils";

export enum AllocationDimensionOwnerObject {
    Merchant = "Merchant",
    Collection = "Collection",
}

export enum AllocationDimensionKind {
    Generic = "GENERIC",
    Size = "SIZE",
}

// Enum value is index of allocation dimension.
// The way we are wiring allocation dimension make order of allocation dimension matters,
// where dates is first and doors second.
// TODO: we should remove concept of door and dates from FE
export enum AllocationDimensionIndex {
    Date = 0,
    Door = 1,
}

export const AllocationDimensionFields = {
    attributes: ["attributes"],
    dimensionValues: ["dimension_values"],
};

export interface IAllocationDimension {
    id: string;
    revision: number;
    subtype: AllocationDimensionKind;
    active: boolean;
    name: string;
    dimension_values: IAllocationDimensionValue[];
    attributes: {
        keys: ISchemaKey[];
    };
}

export interface AllocationDimensionOptions {
    getOnlyUniqDates?: boolean;
    shipmentsWithUnits?: boolean;
}

export interface AllocationDimension {
    active: boolean;
    included?: boolean;
    id: string;
    name: string;
    group?: string;
}

export interface ICreateAllocationDimension
    extends Omit<IAllocationDimension, "id" | "dimension_values" | "revision"> {}
export interface IUpdateAllocationDimension extends Partial<ICreateAllocationDimension> {}

export enum AllocationDimensionValueFields {
    ID = "id",
    ExternalId = "external_id",
    Name = "name",
    Active = "active",
    Included = "included",
    RetailPriceField = "allocation_retail_price",
    CostPriceField = "allocation_cost_price",
    Notes = "_door_notes",
}

export type IAllocationDimensionValueFields = {
    [field: string]: {
        string_value: string;
    };
};

export interface IAllocationDimensionValue extends AllocationDimension {
    external_id: string;

    fields?: {
        [field: string]: {
            string_value: string;
        };
    };
}
export interface ICreateAllocationDimensionValue extends Omit<IAllocationDimensionValue, "id"> {}
export interface IUpdateAllocationDimensionValue extends Partial<IAllocationDimensionValue> {}

export enum AllocationDimensionOperationKey {
    SetName = "set_name",
    SetActive = "set_active",
    SetAttributes = "set_attributes",
    InsertDimensionValue = "insert_dimension_value",
    SetDimensionValue = "set_dimension_value",
    MoveDimensionValue = "move_dimension_value",
}

export type BuildMutateOperationParam =
    | {
          type: AllocationDimensionOperationKey.SetName;
          value: string;
      }
    | {
          type: AllocationDimensionOperationKey.SetActive;
          value: boolean;
      }
    | {
          type: AllocationDimensionOperationKey.SetAttributes;
          value: {
              keys: ISchemaKey[];
          };
      }
    | {
          type: AllocationDimensionOperationKey.InsertDimensionValue;
          value: {
              index: number;
              value: ICreateAllocationDimensionValue;
          };
      }
    | {
          type: AllocationDimensionOperationKey.SetDimensionValue;
          value: {
              index: number;
              value: IUpdateAllocationDimensionValue;
          };
      }
    | {
          type: AllocationDimensionOperationKey.MoveDimensionValue;
          value: {
              index: number;
              destination_index: number;
          };
      };

export interface IAllocationDimensionOperation {
    [AllocationDimensionOperationKey.SetName]: string;
    [AllocationDimensionOperationKey.SetActive]: boolean;
    [AllocationDimensionOperationKey.SetAttributes]: {
        keys: ISchemaKey[];
    };
    [AllocationDimensionOperationKey.InsertDimensionValue]: {
        index: number;
        value: ICreateAllocationDimensionValue;
    };
    [AllocationDimensionOperationKey.SetDimensionValue]: {
        index: number;
        value: IUpdateAllocationDimensionValue;
    };
    [AllocationDimensionOperationKey.MoveDimensionValue]: {
        index: number;
        destination_index: number;
    };
}

export const buildMutateOperation = (
    operation: AllocationDimensionOperationKey,
    value: IAllocationDimensionOperation[AllocationDimensionOperationKey]
): Partial<IAllocationDimensionOperation> => {
    let operationValue;
    switch (operation) {
        case AllocationDimensionOperationKey.InsertDimensionValue:
        case AllocationDimensionOperationKey.SetDimensionValue:
        case AllocationDimensionOperationKey.MoveDimensionValue:
            operationValue = {
                ...(value as any),
            };
            break;
        default: {
            operationValue = {
                value,
            };
            break;
        }
    }
    // Not intuitive way to reorder. If destination_index > index, destination_index must increment 1
    if (operation === AllocationDimensionOperationKey.MoveDimensionValue) {
        if (operationValue.destination_index > operationValue.index) {
            operationValue.destination_index += 1;
        }
    }

    return {
        [operation]: operationValue,
    };
};

export const allocationDimensionFieldToOperationMap = {
    name: AllocationDimensionOperationKey.SetName,
    active: AllocationDimensionOperationKey.SetActive,
    attributes: AllocationDimensionOperationKey.SetAttributes,
};
export const buildMutateOperationFromAllocationDimension = (
    allocationDimension: IUpdateAllocationDimension
): Array<Partial<IAllocationDimensionOperation>> => {
    const operations = [];
    Object.keys(allocationDimension).forEach((allocationDimensionKey) => {
        if (allocationDimensionFieldToOperationMap[allocationDimensionKey]) {
            operations.push(
                buildMutateOperation(
                    allocationDimensionFieldToOperationMap[allocationDimensionKey],
                    allocationDimension[allocationDimensionKey]
                )
            );
        }
    });
    if (operations.length === 0) {
        throw new Error("Invalid allocation dimension properties");
    }

    return operations;
};

// Normalize allocation dimension on collection level, which is nested inside mutable mixed object.
export const normalizeCollectionAllocationDimension = (ref: any, data: any): IAllocationDimension => ({
    id: ref.object_id,
    revision: ref.revision,
    name: lodashGet(data, ["field_map", "map", "fields", "name", "string_value"]),
    subtype: lodashGet(data, ["field_map", "map", "fields", "subtype", "string_value"]),
    active: lodashGet(data, ["field_map", "map", "fields", "active", "bool_value"]),
    dimension_values: lodashGet(data, ["dimension_values", "list", "values"], []).map(
        (rawDimensionValue) => rawDimensionValue.struct_value
    ),
    attributes: lodashGet(data, ["field_map", "map", "fields", "attributes", "struct_value"]),
});

// Normalize allocation dimension on collection level, which is nested inside mutable mixed object.
export const normalizeCollectionAllocationDimensionJS = (ref: any, data: any): IAllocationDimension => ({
    id: ref.object_id,
    revision: ref.revision,
    name: data.field_map?.map?.fields?.name?.string_value,
    subtype: data.field_map?.map?.fields?.subtype?.string_value,
    active: data.field_map?.map?.fields?.active?.bool_value,
    dimension_values: (data.dimension_values?.list?.values ?? []).map((raw) => raw.struct_value),
    attributes: data.field_map?.map?.fields?.attributes?.struct_value,
});

export const getGetterBreadcrumbs = memoizeOne(
    (genericAllocationDimensions: IAllocationDimension[], depth?: number) => {
        let allocationDimensionValueBreadcrumbs = [];
        let copiedAllocationDimensionValueBreadcrumbs = [];

        // Reverse-loop through allocation dimensions
        let allocationDimensionLoopCounter = depth || genericAllocationDimensions.length;

        // Negative means ignore n index from the end
        if (depth < 0) {
            allocationDimensionLoopCounter = genericAllocationDimensions.length - depth * -1;
        }

        const seenKey = {};
        let isFirstLoop = true;
        while (allocationDimensionLoopCounter > 0) {
            copiedAllocationDimensionValueBreadcrumbs = allocationDimensionValueBreadcrumbs;
            allocationDimensionValueBreadcrumbs = [];
            for (const allocationDimensionValue of genericAllocationDimensions[allocationDimensionLoopCounter - 1]
                .dimension_values) {
                const key = `${genericAllocationDimensions[allocationDimensionLoopCounter - 1].id}_${
                    allocationDimensionValue.id
                }`;
                if (!allocationDimensionValue.included || !allocationDimensionValue.active || seenKey[key]) {
                    continue;
                }

                seenKey[key] = true;

                // If there are previous breadcrumbs, prepend to each
                if (!isFirstLoop) {
                    for (const allocationDimensionValueBreadcrumb of copiedAllocationDimensionValueBreadcrumbs) {
                        allocationDimensionValueBreadcrumbs.push([
                            allocationDimensionValue.id,
                            ...allocationDimensionValueBreadcrumb,
                        ]);
                    }
                } else {
                    allocationDimensionValueBreadcrumbs.push([allocationDimensionValue.id]);
                }
            }
            isFirstLoop = false;
            allocationDimensionLoopCounter -= 1;
        }

        return allocationDimensionValueBreadcrumbs;
    }
);

// Build map of dimension value name index for sorting. It will only take active and included value.
type IGetDimensionValueNamesOrderMapFunc = (allocationDimension: IAllocationDimension) => { [id: string]: number };
export const getDimensionValueNamesOrderMap = lodashMemoize((allocationDimension: IAllocationDimension) => {
    let counter = 0;
    return allocationDimension.dimension_values.reduce((acc, dimensionValue, index) => {
        if (dimensionValue.active && dimensionValue.included) {
            acc[dimensionValue.name] = counter;
            counter++;
        }

        return acc;
    }, {});
}) as IGetDimensionValuesOrderMapFunc;

// Build map of dimension value index for sorting. It will only take active and included value.
type IGetDimensionValuesOrderMapFunc = (allocationDimension: IAllocationDimension) => { [id: string]: number };
export const getDimensionValuesOrderMap = lodashMemoize((allocationDimension: IAllocationDimension) => {
    let counter = 0;
    return allocationDimension.dimension_values.reduce((acc, dimensionValue, index) => {
        if (dimensionValue.active && dimensionValue.included) {
            acc[dimensionValue.id] = counter;
            counter++;
        }

        return acc;
    }, {});
}) as IGetDimensionValuesOrderMapFunc;

export interface IItemProxy extends IGridItem {
    quantity: number;
    proxyIndex: string;
    breadcrumbs: string[];
    positionValues: number[];
}
export const explodeRowByAllocationDimensionsLegacy = (
    row: IGridItem,
    bagType: ItemBagType,
    genericAllocationDimensions: IAllocationDimension[],
    selectedGenericAllocationDimensions: IAllocationDimension[],
    // To only show filtered dimensions
    filterDimensionValues?:
        | {}
        | {
              [dimensionId: string]: string[];
          }
): IItemProxy[] | IGridItem[] => {
    let bagName;
    const rows = [row];
    switch (bagType) {
        case ItemBagType.Allocations:
            bagName = Item.ALLOCATIONS;
            break;
        case ItemBagType.Bulks:
            bagName = Item.BULKS;
            break;
        default:
            return rows;
    }

    const bag = lodashGet(row, bagName) as IBagTable | undefined;
    if (!bag) {
        return rows;
    }

    // Positions of excluded dimensions to check
    const filteringValuesPositions = [];
    // Positions of selected dimensions to check
    const positions = [];
    // Find positions in descending order because last dimension is the lowest identifier
    for (let i = genericAllocationDimensions.length - 1; i >= 0; i--) {
        if (
            selectedGenericAllocationDimensions.some(
                (allocationDimension) => allocationDimension.id === genericAllocationDimensions[i].id
            )
        ) {
            positions.push(i);
        }

        if (filterDimensionValues?.[genericAllocationDimensions[i].id]?.length > 0) {
            filteringValuesPositions.push(i);
        }
    }

    let indexCounter = 0;
    const pairMap = {};
    const proxyRows = [];
    for (const bagRow of bag.r) {
        indexCounter += 1;
        const qty = bagRow.c[bagRow.c.length - 1];

        // Skip bag item that doesn't have same length as number of dimensions. +1 at the end is quantity
        if (bagRow.c.length !== genericAllocationDimensions.length + 1) {
            continue;
        }

        // Skip bag item that has inactive or excluded value
        if (
            bagRow.c.slice(0, bagRow.c.length - 1).some((column, index) => {
                // When dimension value is not in `valuesOrderMap`, it is not active or not included. Skip that.
                const valuesOrderMap = getDimensionValuesOrderMap(genericAllocationDimensions[index]);
                return valuesOrderMap[column] === undefined;
            })
        ) {
            continue;
        }

        // Skip zero. Purposely less-strict check for 0
        if (!qty || qty == "0") {
            continue;
        }

        const isValueFilteredOut =
            filteringValuesPositions.length > 0 &&
            filteringValuesPositions.some((filteringValuesPosition) => {
                const filteringValues = filterDimensionValues[genericAllocationDimensions[filteringValuesPosition].id];
                return !filteringValues.includes(bagRow.c[filteringValuesPosition]);
            });

        if (isValueFilteredOut) {
            continue;
        }

        const sparsePositions = [];
        let positionInList = 0;
        for (let loopIndex = 0; loopIndex < selectedGenericAllocationDimensions.length; loopIndex++) {
            const index = genericAllocationDimensions.findIndex(
                (allocationDimension) => allocationDimension.id === selectedGenericAllocationDimensions[loopIndex].id
            );
            sparsePositions[index] = bagRow.c[index];

            const valuesOrderMap = getDimensionValuesOrderMap(selectedGenericAllocationDimensions[loopIndex]);
            positionInList = positionInList * Object.keys(valuesOrderMap).length + valuesOrderMap[bagRow.c[index]];
        }

        const pairKey = positions.reduce((acc, position) => {
            return `${acc}_${bagRow.c[position]}`;
        }, "");

        // If group already exists, skip creating proxy as we only want to keep one row to represent.
        // But add qty to that
        if (pairMap[pairKey]) {
            pairMap[pairKey].quantity += safeParseFloat(qty, 0);
            pairMap[pairKey].positionValues.push(indexCounter - 1);
            continue;
        }

        pairMap[pairKey] = new Proxy<IItemProxy>(row as IItemProxy, {
            get: function (target, prop, receiver) {
                if (prop === "quantity") {
                    return this.quantity;
                }

                if (prop === "proxyIndex") {
                    return this.proxyIndex;
                }

                if (prop === "positionValues") {
                    return this.positionValues;
                }

                if (prop === "breadcrumbs") {
                    return this.breadcrumbs;
                }

                return Reflect.get(target, prop, receiver);
            },
            set(target, prop, value) {
                if (prop === "proxyIndex") {
                    this.proxyIndex = value;
                    return true;
                }

                if (prop === "quantity") {
                    this.quantity = value;
                    return true;
                }

                if (prop === "positionValues") {
                    this.positionValues = value;
                    return true;
                }

                if (prop === "breadcrumbs") {
                    this.breadcrumbs = value;
                    return true;
                }

                return Reflect.set(target, prop, value);
            },
        });
        pairMap[pairKey].quantity = parseFloat(qty);
        pairMap[pairKey].breadcrumbs = sparsePositions;
        pairMap[pairKey].positionValues = [indexCounter - 1];

        // `proxyRows` is sparse array. To respect dimension values order, we store it in the correct order then filter out empty element
        proxyRows[positionInList] = pairMap[pairKey];
    }

    if (proxyRows.length > 0) {
        return proxyRows.reduce((acc, proxyRow) => {
            if (proxyRow) {
                // Assign `proxyIndex` so we can know which exploded row is the first.
                // This can be useful to count exploded rows as SINGLE row.
                proxyRow.proxyIndex = acc.length;
                acc.push(proxyRow);
            }

            return acc;
        }, []);
    } else {
        return rows;
    }
};

/**
 * Explode a row based on permutation of dimension values. Eg: a row can have units in these permutations of dimensions: Delivery1-Door1, Delivery1-Door2, Delivery2-Door1
 * This row will be exploded to 3 rows
 * @param row Collection row
 * @param allocationBag The allocation bag itself
 * @param bagType Type of bag
 * @param genericAllocationDimensions Available Dimensions
 * @param selectedGenericAllocationDimensions Active dimensions to explode by
 * @param filterDimensionValues Dimension values that are being filtered on
 * @returns
 */
export const explodeRowByAllocationDimensions = (
    row: IGridItem,
    allocationBag: AllocationBag,
    bagType: ItemBagType,
    genericAllocationDimensions: IAllocationDimension[],
    selectedGenericAllocationDimensions: IAllocationDimension[],
    // To only show filtered dimensions
    filterDimensionValues?:
        | {}
        | {
              [dimensionId: string]: string[];
          }
): IItemProxy[] | IGridItem[] => {
    const rows = [row];

    if (!allocationBag) {
        return rows;
    }

    const rowId = lodashGet(row, Item.FIELDS.ID);
    // Positions of excluded dimensions to check
    const filteringValuesPositions = [];
    // Positions of selected dimensions to check
    const positions = [];
    // Find positions in descending order because last dimension is the lowest identifier
    for (let i = genericAllocationDimensions.length - 1; i >= 0; i--) {
        if (
            selectedGenericAllocationDimensions.some(
                (allocationDimension) => allocationDimension.id === genericAllocationDimensions[i].id
            )
        ) {
            positions.push(i);
        }

        if (filterDimensionValues?.[genericAllocationDimensions[i].id]?.length > 0) {
            filteringValuesPositions.push(i);
        }
    }

    let indexCounter = 0;
    const pairMap = {};
    const proxyRows = [];

    forEachAllocationBag(allocationBag, rowId, AllocationBagType[bagType], (dimensionValueIds, qty) => {
        indexCounter++;
        if (dimensionValueIds.length !== genericAllocationDimensions.length) {
            return;
        }

        if (
            dimensionValueIds.some((column, index) => {
                // When dimension value is not in `valuesOrderMap`, it is not active or not included. Skip that.
                const valuesOrderMap = getDimensionValuesOrderMap(genericAllocationDimensions[index]);
                return valuesOrderMap[column] === undefined;
            })
        ) {
            return;
        }

        // Skip zero. Purposely less-strict check for 0
        if (!qty || qty == "0") {
            return;
        }

        const isValueFilteredOut =
            filteringValuesPositions.length > 0 &&
            filteringValuesPositions.some((filteringValuesPosition) => {
                const filteringValues = filterDimensionValues[genericAllocationDimensions[filteringValuesPosition].id];
                return !filteringValues.includes(dimensionValueIds[filteringValuesPosition]);
            });

        if (isValueFilteredOut) {
            return;
        }

        const sparsePositions = [];
        let positionInList = 0;
        for (let loopIndex = 0; loopIndex < selectedGenericAllocationDimensions.length; loopIndex++) {
            const index = genericAllocationDimensions.findIndex(
                (allocationDimension) => allocationDimension.id === selectedGenericAllocationDimensions[loopIndex].id
            );
            sparsePositions[index] = dimensionValueIds[index];

            const valuesOrderMap = getDimensionValuesOrderMap(selectedGenericAllocationDimensions[loopIndex]);
            positionInList =
                positionInList * Object.keys(valuesOrderMap).length + valuesOrderMap[dimensionValueIds[index]];
        }

        const pairKey = positions.reduce((acc, position) => {
            return `${acc}_${dimensionValueIds[position]}`;
        }, "");

        // If group already exists, skip creating proxy as we only want to keep one row to represent.
        // But add qty to that
        if (pairMap[pairKey]) {
            pairMap[pairKey].quantity += safeParseFloat(qty, 0);
            pairMap[pairKey].positionValues.push(indexCounter - 1);
            return;
        }

        pairMap[pairKey] = new Proxy<IItemProxy>(row as IItemProxy, {
            get: function (target, prop, receiver) {
                if (prop === "quantity") {
                    return this.quantity;
                }

                if (prop === "proxyIndex") {
                    return this.proxyIndex;
                }

                if (prop === "positionValues") {
                    return this.positionValues;
                }

                if (prop === "breadcrumbs") {
                    return this.breadcrumbs;
                }

                return Reflect.get(target, prop, receiver);
            },
            set(target, prop, value) {
                if (prop === "proxyIndex") {
                    this.proxyIndex = value;
                    return true;
                }

                if (prop === "quantity") {
                    this.quantity = value;
                    return true;
                }

                if (prop === "positionValues") {
                    this.positionValues = value;
                    return true;
                }

                if (prop === "breadcrumbs") {
                    this.breadcrumbs = value;
                    return true;
                }

                return Reflect.set(target, prop, value);
            },
        });
        pairMap[pairKey].quantity = parseFloat(qty);
        pairMap[pairKey].breadcrumbs = sparsePositions;
        pairMap[pairKey].positionValues = [indexCounter - 1];

        // `proxyRows` is sparse array. To respect dimension values order, we store it in the correct order then filter out empty element
        proxyRows[positionInList] = pairMap[pairKey];
    });

    if (proxyRows.length > 0) {
        return proxyRows.reduce((acc, proxyRow) => {
            if (proxyRow) {
                // Assign `proxyIndex` so we can know which exploded row is the first.
                // This can be useful to count exploded rows as SINGLE row.
                proxyRow.proxyIndex = acc.length;
                acc.push(proxyRow);
            }

            return acc;
        }, []);
    } else {
        return rows;
    }
};

const allocationDimensionValueGetter = (
    allocationDimensionIndex: number,
    allocationDimensions: IAllocationDimension[],
    isRollupMode: boolean,
    params: ValueGetterParams
): string | undefined => {
    const { node, data, column } = params;
    const allocationDimension: IAllocationDimension = allocationDimensions[allocationDimensionIndex];
    const allocationDimensionValueId = get(data, ["fields", column.getColId(), "string_value"], "");

    if (node && node.group) {
        if (isRollupMode) {
            return (
                allocationDimension.dimension_values.find(
                    (dimensionValue) => dimensionValue.id === allocationDimensionValueId
                )?.name ?? ""
            );
        }
        return undefined;
    }

    const valueId = data?.breadcrumbs?.[allocationDimensionIndex];
    // Render comma-separated dimension values for dimension column
    if (!valueId) {
        const values = {};
        (data?.positionValues || []).forEach((positionValue) => {
            const inBagValueId = data.bulks.r[positionValue].c[allocationDimensionIndex];
            if (!values[data.bulks.r[positionValue][allocationDimensionIndex]]) {
                const allocationDimensionValue = allocationDimension.dimension_values.find(
                    (dimensionValue) => dimensionValue.id === inBagValueId
                );

                if (allocationDimensionValue) {
                    values[inBagValueId] = allocationDimensionValue.name;
                }
            }
        });

        return Object.values(values).join(", ");
    }

    const allocationDimensionValue = allocationDimension.dimension_values.find(
        (dimensionValue) => dimensionValue.id === valueId
    );
    return allocationDimensionValue?.name;
};

export const getAllocationDimensionValueGetter = (
    allocationDimensionIndex: number,
    allocationDimensions: IAllocationDimension[],
    isRollupMode: boolean
) => {
    return allocationDimensionValueGetter.bind(this, allocationDimensionIndex, allocationDimensions, isRollupMode);
};

export const matchBreadcrumbs = (breadcrumbs1: string[], breadcrumbs2: string[]) => {
    // Breadcrumbs1 is sparse array. Take undefined as wildcard.
    // For-of doesn't work with sparse array
    for (let i = 0; i < breadcrumbs1.length; i += 1) {
        if (breadcrumbs1[i] && breadcrumbs1[i] !== breadcrumbs2[i]) {
            return false;
        }
    }

    return true;
};

// HACK. This is hardcoding there will always be two allocation dimensions.
export const getAllocationDimensionByIndex = (allocationDimensions, allocationDimensionIndex) => {
    if (allocationDimensions.length >= 2) {
        return allocationDimensions[allocationDimensionIndex];
    }

    return allocationDimensions[allocationDimensionIndex - 1] || {};
};

export const ROLLUP_ALLOCATION_DIMENSION_DELIMITER = "==";

export const getUserDefinedClassificationName = (classification: string, collectionId: string) =>
    `${classification}${ROLLUP_ALLOCATION_DIMENSION_DELIMITER}${collectionId}`;

export const isAllocationDimensionId = (id: string) => new RegExp(ALLOCATION_DIMENSION_ID_PREFIX, "i").test(id);

/**
 * Returns delivery dimensions only from list of given dimensions.
 * Dimensions can be delivery, doors and sizes.
 * @param allocationDimensions
 * @returns - delivery dimension or null.
 */
export const getDeliveryDimensions = (allocationDimensions: IAllocationDimension[]) =>
    allocationDimensions.find((dimension) => dimension.name === "Deliveries");

/**
 * Returns delivery dimensions that are visible/active, i.e. if selected from product collection tool bar.
 * @param allocationDimensions
 * @param {string[]} deliveryDimensionFilters - array of delivery dimension ids.
 * @returns - delivery dimensions in active filters. undefined if delivery dimensions are empty.
 */
export const getActiveDeliveryDimensions = (
    allocationDimensions: IAllocationDimension[],
    deliveryDimensionFilters: string[]
): IAllocationDimensionValue[] => {
    const deliveryDimensions = getDeliveryDimensions(allocationDimensions);
    return deliveryDimensionFilters.length
        ? deliveryDimensions?.dimension_values.filter((allocDim) => deliveryDimensionFilters.includes(allocDim.id))
        : deliveryDimensions?.dimension_values;
};

export enum AllocationBagType {
    allocations = 1,
    bulks = 2,
    pack_allocations = 3,
    inventory_units = 4,
    po_creation_exports = 5,
    target = 6,
}

/*
Mutable mixed bag key & path nomenclature
const bagValue = {                                AllocationBag
    '74d0cc6e-51da-4844-823f-03cfb36145dd#2': {   AllocationBagKey = itemId#type
        map: {
            fields: {
                '20#1': {                         BreadcrumbKey is the key, BagValues is the content
                    string_value: '1'
                },
                '27#1': {string_value: '2'},
                '27#2': {string_value: '3'},
            }
        }
    },
    aa434968-c928-4908-9d99-75759809f44c#2: {map: {…}},
}
[allocationBagKey, 'map', 'fields', breadcrumbKey] = CompoundPath
[allocationBagKey, 'map', 'fields', breadcrumbKey, 'string_value'] = ValuePath
*/

type AllocationBagKey = string;
type BreadcrumbKey = string;
export type BagValues = Map<BreadcrumbKey, Map<"string_value", string>>;
export type AllocationBag = Map<AllocationBagKey, Map<"map", Map<"fields", BagValues>>>;
export const BAG_VALUE_PARAMS_SEPARATOR = "#";

interface MutateBagValueCallback {
    (type: AllocationBagType, itemId: string, breadcrumb: string[], value: number): void;
}

/**
 * Generate path to access content of row's bag, which contains list of available quantities per dimensions
 * @param type Type of bag
 * @param itemId Id of item, eg: assortment row's id
 * @returns
 */
export const getAllocationBagPath = lodashMemoize(
    (type: AllocationBagType, itemId: string) => {
        return [[itemId, type].join(BAG_VALUE_PARAMS_SEPARATOR), "map", "fields"];
    },
    (type, itemId) => `${type}_${itemId}`
);

/**
 * Generate direct path to get quantity
 * @param type Bag type
 * @param itemId Id of item, eg: assortment row's id
 * @param breadcrumb Dimension ids of quantity to retrive
 * @param includeValueKey Flag whether to return path of primitive value or wrapped value. Eg: "1" vs { "string_value": "1" }
 * @returns
 */
export const getAllocationBagValuePath = (
    type: AllocationBagType,
    itemId: string,
    breadcrumb: string | string[],
    includeValueKey: boolean = true
): string[] => {
    const allocationBagPath = getAllocationBagPath(type, itemId);
    const breadcrumbKey = Array.isArray(breadcrumb) ? breadcrumb.join(BAG_VALUE_PARAMS_SEPARATOR) : breadcrumb;
    const path = allocationBagPath.concat([breadcrumbKey]);

    if (includeValueKey) {
        path.push("string_value");
    }

    return path;
};

/**
 * Get row's bag, which contains list of available quantities per dimensions. Eg: quantity of delivery "January" for door "NYC".
 * @param type Type of bag
 * @param bagValue Bag itself
 * @param itemId Id of item, eg: assortment row's id
 * @returns
 */
export const getBagValuesByRowId = (type: AllocationBagType, bagValue: AllocationBag, itemId: string): BagValues => {
    return bagValue?.getIn(getAllocationBagPath(type, itemId), Map()) ?? Map();
};

/**
 * Simple, get a quantity by bag type, row id and dimensions. Eg: give me quantity of row "A" for delivery "January" for door "NYC".
 * @param type Type of bag
 * @param allocationBag Bag itself
 * @param itemId Id of item, eg: assortment row's id
 * @param breadcrumb Dimension ids of quantity to retrive
 * @returns
 */
export const getAllocationBagValue = (
    type: AllocationBagType,
    allocationBag: AllocationBag,
    itemId: string,
    breadcrumb: string | string[]
): number | undefined => {
    const stringValue = allocationBag?.getIn(getAllocationBagValuePath(type, itemId, breadcrumb));

    // if value is not found or 0, return undefined so that cell is empty
    return safeParseFloat(stringValue, 0) || undefined;
};

/**
 * Simple, get a quantity by dimensions. Eg: give me quantity of delivery "January" for door "NYC".
 * @param bagValues Content of allocation bag
 * @param breadcrumb Dimension ids of quantity to retrive
 * @returns
 */
export const getBagValue = (bagValues: BagValues, breadcrumb: string[]): number | undefined => {
    const stringValue = bagValues.getIn([breadcrumb.join(BAG_VALUE_PARAMS_SEPARATOR), "string_value"]);

    // if value is not found or 0, return undefined so that cell is empty
    return safeParseInt(stringValue, 0) || undefined;
};

let setBagValueOperationQueue = [];

const handleBagValueOperationQueue = debounce(() => {
    if (setBagValueOperationQueue.length < MAX_MUTATION_OPERATION_LIMIT) {
        let currentBag;
        let lastCallback;
        setBagValueOperationQueue.forEach((bagValueOperation, idx) => {
            const { type, bagValue, itemId, breadcrumb, value, callback } = bagValueOperation;
            if (!+value) {
                const path = getAllocationBagValuePath(type, itemId, breadcrumb);

                if (bagValue.hasIn(path)) {
                    currentBag = bagValue.deleteIn(path);
                } else {
                    currentBag = bagValue;
                }
            } else {
                const path = getAllocationBagValuePath(type, itemId, breadcrumb, false);
                currentBag = bagValue.setIn(path, fromJS({ string_value: value.toString() }));
            }

            if (idx === setBagValueOperationQueue.length - 1) {
                lastCallback = callback;
            }
        });
        lastCallback(currentBag);
    }
    setBagValueOperationQueue = [];
}, 10);

/**
 * Set a quantity to dimensions. Eg: set quantity for row "A" for delivery "January" for door "NYC". This method is debounced by 50 ms,
 * calls in between this time period will be queued and executed together
 * @param type Type of bag
 * @param bagValue Bag itself
 * @param itemId Id of item, eg: assortment row's id
 * @param breadcrumb Dimension ids of quantity to retrive
 * @param value Value of to set
 * @param callback Callback to be used set the datasource with the latest mutated object
 * @returns
 */
export const setBagValueDebounced = (
    type: AllocationBagType,
    bagValue: AllocationBag,
    itemId: string,
    breadcrumb: string[],
    value: number,
    callback: (allocationBag: AllocationBag) => void
) => {
    setBagValueOperationQueue.push({
        type,
        bagValue,
        itemId,
        breadcrumb,
        value,
        callback,
    });
    handleBagValueOperationQueue();
};

/**
 * Set a quantity to dimensions. Eg: set quantity for row "A" for delivery "January" for door "NYC".
 * @param type Type of bag
 * @param bagValue Bag itself
 * @param itemId Id of item, eg: assortment row's id
 * @param breadcrumb Dimension ids of quantity to retrive
 * @param value Value of to set
 * @param callback Callback to be used for easier mutation to server
 * @returns
 */
export const setBagValue = (
    type: AllocationBagType,
    bagValue: AllocationBag,
    itemId: string,
    breadcrumb: string[],
    value: number,
    callback?: MutateBagValueCallback
): AllocationBag => {
    if (!+value) {
        callback && callback(type, itemId, breadcrumb, 0);
        const path = getAllocationBagValuePath(type, itemId, breadcrumb);

        if (bagValue.hasIn(path)) {
            return bagValue.deleteIn(path);
        } else {
            return bagValue;
        }
    }

    callback && callback(type, itemId, breadcrumb, +value);

    const path = getAllocationBagValuePath(type, itemId, breadcrumb, false);
    return bagValue.setIn(path, fromJS({ string_value: value.toString() }));
};

/**
 * Clear quantities for a row.
 * @param type Type of bag
 * @param bagValue Bag itself
 * @param itemId Id of item, eg: assortment row's id
 * @param callback Callback to be used for easier mutation to server
 * @returns
 */
export const clearBagValues = (
    type: AllocationBagType,
    bagValue: AllocationBag,
    itemId: string,
    callback?: MutateBagValueCallback
): AllocationBag => {
    let mutatedBagValue = bagValue;
    const bagValues = getBagValuesByRowId(type, mutatedBagValue, itemId);
    bagValues.forEach((_bagValue, key) => {
        const breadcrumb = key.split(BAG_VALUE_PARAMS_SEPARATOR);
        callback && callback(type, itemId, breadcrumb, 0);

        mutatedBagValue = mutatedBagValue.deleteIn(getAllocationBagPath(type, itemId).concat(key));
    });

    return mutatedBagValue;
};

/**
 * Return last key of a compound key
 * @param key Compound key to parse from
 * @returns
 */
export const parseLastKeyFromCompoundKey = (key: string) => {
    const matches = key.match(new RegExp(`${BAG_VALUE_PARAMS_SEPARATOR}([^#]+)$`, "i"));
    return matches ? matches[1] : undefined;
};

/**
 * Transform flat dimension quantity into allocation bag. Eg: ["delivery11", "door1", "size1", "50"] to { "delivery1#door1#size#": { "string_value": "50" } }
 * @param key Compound key to parse from
 * @returns
 */
export const flatToBagValues = (dimensionQtys: string[][]) => {
    return dimensionQtys.reduce((acc, dimensionQty) => {
        const cloned = [...dimensionQty];
        const quantity = cloned.pop();
        const compoundKey = cloned.join(BAG_VALUE_PARAMS_SEPARATOR);
        acc = acc.set(compoundKey, fromJS({ string_value: quantity.toString() }));

        return acc;
    }, Map() as BagValues);
};

/**
 * This function will:
 * 1. Get the allocation bag using path [itemId#type, map, fields]
 * 2. Run callback on each element of the allocation bag
 * @param bagValue The whole bag, includes multiple allocation bags
    const bag = {
        '74d0cc6e-51da-4844-823f-03cfb36145dd#2': {
            map: {
                fields: {
                    '20#1': {string_value: '1'},
                    '27#1': {string_value: '2'},
                }
            }
        },
        aa434968-c928-4908-9d99-75759809f44c#2: {
            map: {
                fields: {
                    '21#2': {string_value: '3'}
                }
            }
        },
    }
 * @param itemId Item ID or product ID
 * @param bagType Bag type, e.g. 1 for allocations, 2 for bulks
 * @param callback A function which you can supply, accepts
 * the dimensions and value of each allocation element
    Example:
    forEachBag(bag, '74d0cc6e-51da-4844-823f-03cfb36145dd', '2', (path, value) => {...})
    will execute the callback function with:
    ['20','1'], '1'
    ['27','1'], '2'
    ...
 */
export const forEachAllocationBag = (
    allocationBag: AllocationBag,
    itemId: string,
    bagType: AllocationBagType,
    callback: (dimensions: string[], value: string) => void
) => {
    const bagValues = getBagValuesByRowId(bagType, allocationBag, itemId);

    bagValues.forEach((bagValue, breadcrumbKey) => {
        const dimensions = breadcrumbKey.split(BAG_VALUE_PARAMS_SEPARATOR);
        if (bagValue) {
            callback(dimensions, bagValue.get("string_value"));
        }
    });
};

/**
 * Check whether bag is empty
 * @param bagValues Content of the bag
 * @returns
 */
export const areBagValuesEmpty = (bagValues: BagValues) =>
    !bagValues.size || bagValues.every((bagValue) => (bagValue?.getIn(["string_value"]) ?? "0") >>> 0 === 0);

/**
 * Compare if size units between bags are equal
 * @param bagValuesA Content of bags to compare
 * @param bagValuesB Content of bags to compare
 * @returns
 */
export const areBagValuesEqual = (bagValuesA?: BagValues, bagValuesB?: BagValues): boolean => {
    // Let itemA be the reference item.

    if (!bagValuesA || !bagValuesB) {
        return (
            (!bagValuesA && !bagValuesB) ||
            (!bagValuesA && areBagValuesEmpty(bagValuesB)) ||
            (!bagValuesB && areBagValuesEmpty(bagValuesA))
        );
    }

    const sizeASum = {};
    const sumA = bagValuesA.reduce((acc, bagValue, key) => {
        const value = safeParseInt(bagValue?.getIn(["string_value"]) ?? "0", 0);
        // size is always stored as second last key
        const size = key.split(BAG_VALUE_PARAMS_SEPARATOR).pop();
        if (size !== undefined) {
            sizeASum[size] = (sizeASum[size] ?? 0) + value;
            return acc + value;
        }

        return acc;
    }, 0);

    const sizeBSum = {};
    const sumB = bagValuesB.reduce((acc, bagValue, key) => {
        const value = safeParseInt(bagValue?.getIn(["string_value"]) ?? "0", 0);
        // size is always stored as second last key
        const size = key.split(BAG_VALUE_PARAMS_SEPARATOR).pop();
        if (size !== undefined) {
            sizeBSum[size] = (sizeBSum[size] ?? 0) + value;
            return acc + value;
        }

        return acc;
    }, 0);

    return sumA === sumB && Object.keys(sizeASum).every((size) => (sizeASum[size] ?? 0) === (sizeBSum[size] ?? 0));
};

/**
 * Sum quantity by bag type and row id
 * @param bagType
 * @param allocationBag
 * @param itemId
 * @returns
 */
export const sumAllocationBagByRowId = (
    bagType: AllocationBagType,
    allocationBag: AllocationBag,
    itemId: string
): number => {
    let total = 0;

    if (allocationBag) {
        forEachAllocationBag(allocationBag, itemId, bagType, (_dimensionValues, quantity) => {
            total += safeParseInt(quantity, 0);
        });
    }
    return total;
};

/**
 * Check if bag values has zero quantity
 * @param bagValues
 * @returns
 */
export const isEmptyBagValues = (bagValues: BagValues): boolean => {
    // Short-circuit if any of the value is > 0
    return !bagValues.some((bagValue) => safeParseInt(bagValue?.getIn(["string_value"]) ?? 0, 0) > 0);
};

export const getAllocationBagKind = (bagType: AllocationBagType | string) => AllocationBagType[bagType];

/**
 * Sync Bulks bag with sum of allocation quantities per dimensions
 * @param allocationBag The whole allocation bag
 * @param rowId Id of row to sync
 */
export const syncBulksWithAllocations = (allocationBag: AllocationBag, rowId: string) => {
    const allocationBagValues = getBagValuesByRowId(AllocationBagType.allocations, allocationBag, rowId);
    let mutatedAllocationBag = allocationBag.setIn(getAllocationBagPath(AllocationBagType.bulks, rowId), fromJS({}));

    const totalMap = {};
    allocationBagValues.forEach((bagValue, key) => {
        const quantity = safeParseInt(bagValue?.get("string_value"), 0);

        if (quantity > 0) {
            // For bulks, we only need generic dimensions, not size dimension. So we omit last key since it's size dimension.
            const path = key.split(BAG_VALUE_PARAMS_SEPARATOR).slice(0, -1);
            const existingInMap = totalMap[path.join(BAG_VALUE_PARAMS_SEPARATOR)] ?? {
                path,
                quantity: 0,
            };

            totalMap[path.join(BAG_VALUE_PARAMS_SEPARATOR)] = {
                ...existingInMap,
                quantity: existingInMap.quantity + quantity,
            };
        }
    });

    Object.keys(totalMap).forEach((key) => {
        mutatedAllocationBag = setBagValue(
            AllocationBagType.bulks,
            mutatedAllocationBag,
            rowId,
            totalMap[key].path,
            totalMap[key].quantity
        );
    });

    return mutatedAllocationBag;
};

/**
 * Sync Allocations bag with sum of bulks per dimensions to a single size
 * @param allocationBag The whole allocation bag
 * @param rowId Id of row to sync
 * @param size Size Id to sync bulks into
 */
export const syncAllocationsWithBulks = (allocationBag: AllocationBag, rowId: string, size: string) => {
    const bulksBagValues = getBagValuesByRowId(AllocationBagType.bulks, allocationBag, rowId);
    let mutatedAllocationBag = allocationBag.setIn(
        getAllocationBagPath(AllocationBagType.allocations, rowId),
        fromJS({})
    );

    const totalMap = {};
    bulksBagValues.forEach((bagValue, key) => {
        const quantity = safeParseInt(bagValue?.get("string_value"), 0);

        if (quantity > 0) {
            const path = key.split(BAG_VALUE_PARAMS_SEPARATOR).concat([size]);
            mutatedAllocationBag = setBagValue(
                AllocationBagType.allocations,
                mutatedAllocationBag,
                rowId,
                path,
                quantity
            );
        }
    });

    return mutatedAllocationBag;
};

/**
 * Generate mutations necessary to get `sourceAllocationBag` has same bag values as `destAllocationBag`
 * @param sourceAllocationBag
 * @param destAllocationBag
 * @param rowId
 * @param bagType
 * @returns
 */
export const generateReconciliationBagMutations = (
    sourceAllocationBag: AllocationBag,
    destAllocationBag: AllocationBag,
    rowId: string,
    bagType: AllocationBagType
): IMutateAllocationBagValue[] => {
    const sourceBagValues = getBagValuesByRowId(bagType, sourceAllocationBag, rowId);
    const destBagValues = getBagValuesByRowId(bagType, destAllocationBag, rowId);

    return generateReconciliationMutations(sourceBagValues, destBagValues, rowId, bagType);
};

/**
 * Generate mutations necessary to get `sourceBagValues` to be same as 'destBagValues'
 * @param sourceBagValues
 * @param destBagValues
 * @param rowId
 * @param bagType
 * @returns
 */
export const generateReconciliationMutations = (
    sourceBagValues: BagValues,
    destBagValues: BagValues,
    rowId: string,
    bagType: AllocationBagType
): IMutateAllocationBagValue[] => {
    const removeMutations: { [key: string]: IMutateAllocationBagValue } = {};
    const setMutations: { [key: string]: IMutateAllocationBagValue } = {};
    sourceBagValues.forEach((_bagValue, key) => {
        const destBagValue = destBagValues.get(key);
        const path = key.split(BAG_VALUE_PARAMS_SEPARATOR);

        // Override if there is entry in source bag.
        // If entry exists in source bag but not in dest bag, remove it.
        if (destBagValue) {
            const quantity = safeParseFloat(destBagValue?.get("string_value"), 0);
            setMutations[key] = {
                bagType,
                quantity,
                dimensionValues: path,
                rowId,
            };
        } else {
            removeMutations[key] = {
                bagType,
                quantity: 0,
                dimensionValues: path,
                rowId,
            };
        }
    });

    destBagValues.forEach((bagValue, key) => {
        // Only process key that hasn't been seen
        if (!removeMutations[key] && !setMutations[key]) {
            const path = key.split(BAG_VALUE_PARAMS_SEPARATOR);
            const quantity = safeParseFloat(bagValue?.get("string_value"), 0);
            setMutations[key] = {
                bagType,
                quantity,
                dimensionValues: path,
                rowId,
            };
        }
    });

    return Object.values(removeMutations).concat(Object.values(setMutations));
};

/*
 * Generate mutations to sync PO Creation Status based on selected dimension values
 * TODO: Move function to other domain
 * @param allocationBag The whole allocation bag
 * @param itemId
 * @param selectedDimensionValues
 * @returns
 */
export const generatePOCreationStatusMutations = (
    allocationBag: AllocationBag,
    itemId: string,
    selectedDimensionValues: string[][]
): IMutateAllocationBagValue[] => {
    const mutations = [];

    forEachAllocationBag(allocationBag, itemId, AllocationBagType.bulks, (dimensionValues, quantityString) => {
        const quantity = safeParseInt(quantityString, 0);
        let removeFlag = true;
        if (quantity > 0) {
            const isDimensionIncluded = dimensionValues.every((dimensionValueId, index) =>
                selectedDimensionValues[index].includes(dimensionValueId)
            );
            if (isDimensionIncluded) {
                mutations.push({
                    bagType: AllocationBagType.po_creation_exports,
                    // We use `1` as flag of being truthy
                    quantity: 1,
                    rowId: itemId,
                    dimensionValues,
                });
                removeFlag = false;
            }
        }

        if (removeFlag) {
            mutations.push({
                bagType: AllocationBagType.po_creation_exports,
                quantity: 0,
                rowId: itemId,
                dimensionValues,
            });
        }
    });

    return mutations;
};

type IAllocationBagOperation =
    | {
          [OperationType.Set]: {
              item_id?: string;
              target_id?: string;
              value: {
                  allocation_dimension_value: string[];
                  quantity: string;
                  kind: typeof AllocationBagType;
              };
          };
      }
    | {
          [OperationType.Remove]: {
              item_id?: string;
              target_id?: string;
              value: {
                  allocation_dimension_value: string[];
                  quantity: string;
                  kind: typeof AllocationBagType;
              };
          };
      }
    | {
          [OperationType.Clear]: {
              item_id?: string;
              target_id?: string;
              value: {
                  kind: typeof AllocationBagType;
              };
          };
      };

const commandToAbbreviationMap = {
    [OperationType.Set]: "s",
    [OperationType.Remove]: "r",
    [OperationType.Clear]: "c",
};
/**
 * Generate payload to save to server
 * @param mutations
 */
export const createBatchMutations = (mutations: IAllocationBagOperation[], itemIdKey = "item_id") => {
    const batches = [];
    // Map to keep track of existing batch per row id
    const rowIdToIndexMap: { [key: string]: number } = {};
    // Map to keep track of existing operation per dimension values
    const keysToIndexMap: { [key: string]: number[] } = {};
    mutations.forEach((mutation) => {
        const command = Object.keys(mutation)[0];
        const commandAbbreviation = commandToAbbreviationMap[command];
        const rowId = mutation[command][itemIdKey];
        const dimensionValues = [...(mutation[command].value.allocation_dimension_value ?? [])];
        const lastDimensionValue = dimensionValues.pop();

        let batch, operation;
        if (rowIdToIndexMap[rowId] === undefined) {
            batch = {
                [itemIdKey]: rowId,
            };
            batches.push(batch);
            rowIdToIndexMap[rowId] = batches.length - 1;
        } else {
            batch = batches[rowIdToIndexMap[rowId]];
        }

        if (command === OperationType.Clear) {
            operation = mutation[command].value.kind;
            batch[commandAbbreviation] = (batch[commandAbbreviation] ?? []).concat([operation]);
        } else {
            const compositeId = [rowId, command].concat(dimensionValues).join("_");
            if (keysToIndexMap[compositeId] === undefined) {
                operation = {
                    p: dimensionValues,
                    a: {},
                };

                batch[commandAbbreviation] = (batch[commandAbbreviation] ?? []).concat([operation]);
                keysToIndexMap[compositeId] = [rowIdToIndexMap[rowId], batch[commandAbbreviation].length - 1];
            } else {
                const cachedRowId = keysToIndexMap[compositeId][0];
                const cachedOperationId = keysToIndexMap[compositeId][1];
                operation = batches[cachedRowId][commandAbbreviation][cachedOperationId];
            }

            operation.a[lastDimensionValue.toString()] = {
                ...(operation.a[lastDimensionValue.toString()] ?? { data: [] }),
            };
            operation.a[lastDimensionValue.toString()].data.push({
                k: mutation[command].value.kind,
                q: command === OperationType.Remove ? "" : mutation[command].value.quantity,
            });
        }
    });

    return batches;
};

/**
 * Replace bag values of a row
 * @param type Type of bag
 * @param allocationBag Bag itself
 * @param itemId Id of item, eg: assortment row's id
 * @param bagValues Bag values to replace
 * @returns
 */
export const mutateBagValues = (
    type: AllocationBagType,
    allocationBag: AllocationBag,
    itemId: string,
    bagValues: BagValues
): AllocationBag => {
    const path = getAllocationBagPath(type, itemId);
    let mutatedAllocationBag = allocationBag;
    mutatedAllocationBag = mutatedAllocationBag.setIn(path, bagValues);

    return mutatedAllocationBag;
};

export const getItemsToRemoveOldValuesInAllowedValue = (
    items: { field: string; value: string; oldValue?: string; shouldDeleteOldValueFromAllowedValues?: boolean }[]
) => {
    const itemsToRemoveOldValueInAllowedValue = {};

    items.forEach((item) => {
        const oldValueToIncludeInArray = item.oldValue?.toString()?.toLocaleLowerCase();
        if (item.shouldDeleteOldValueFromAllowedValues && oldValueToIncludeInArray !== undefined) {
            if (itemsToRemoveOldValueInAllowedValue.hasOwnProperty(item.field)) {
                if (!itemsToRemoveOldValueInAllowedValue[item.field].includes(oldValueToIncludeInArray)) {
                    itemsToRemoveOldValueInAllowedValue[item.field].push(oldValueToIncludeInArray);
                }
            } else {
                itemsToRemoveOldValueInAllowedValue[item.field] = [oldValueToIncludeInArray];
            }
        }
    });

    return itemsToRemoveOldValueInAllowedValue;
};

export const transformDoorAttributesFiltersForRollup = (
    collectionId: string,
    attributeFilters: IAttributeFilters,
    allocationDimensionAttributes: ISchemaKey[]
): IAttributeFilters => {
    const updatedAttributeFilters = { ...attributeFilters };

    Object.keys(updatedAttributeFilters).forEach((attributeFilterKey) => {
        const currentAllocationDimensionAttribute = allocationDimensionAttributes.find(
            (allocationDimensionAttribute) => allocationDimensionAttribute.field_name === attributeFilterKey
        );

        if (currentAllocationDimensionAttribute && currentAllocationDimensionAttribute.display?.is_user_defined) {
            updatedAttributeFilters[getUserDefinedClassificationName(attributeFilterKey, collectionId)] =
                updatedAttributeFilters[attributeFilterKey];
            delete updatedAttributeFilters[attributeFilterKey];
        }
    });

    return updatedAttributeFilters;
};

export const defaultAllocationDimensions: ICreateAllocationDimension[] = [
    {
        name: "Deliveries",
        subtype: AllocationDimensionKind.Generic,
        active: false,
        attributes: {
            keys: [],
        },
    },
    {
        name: "Doors",
        subtype: AllocationDimensionKind.Generic,
        active: false,
        attributes: {
            keys: [],
        },
    },
    {
        name: "Size",
        subtype: AllocationDimensionKind.Size,
        active: false,
        attributes: {
            keys: [],
        },
    },
];

export const generateAllocationDimensionAttribute = (doorGroupingName, isUserDefined = true, allowed_values = []) => {
    const allocationDimensionAttribute: ISchemaKey = {
        display: {
            enable_aggregation: false,
            enable_group_by: false,
            enable_pivot: false,
            hidden: false,
            is_user_defined: isUserDefined,
            read_only: false,
        },
        formula: "",
        value_type: ValueTypes.STRING,
        display_name: sanitizeDimensionAttribute.displayName(doorGroupingName),
        hidden: false,
        field_name: sanitizeDimensionAttribute.fieldName(doorGroupingName),
        field_value_delimiter: "",
        kind: Kinds.ALLOCATION_DIMENSION_CLASSIFICATION,
        constraint: {
            allowed_values: allowed_values,
            freeform: true,
        },
        grid_column: undefined,
    };

    return allocationDimensionAttribute;
};

export const getDeliveriesFromAllocationDimension = (dimensionValues: AllocationDimension[]) => {
    const validDeliveries = dimensionValues.reduce((acc, dimension) => {
        const { included, active } = dimension;
        if (active && included) {
            acc.push(dimension);
        }
        return acc;
    }, []);

    return validDeliveries;
};
